import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {alignmentStatus, greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import type {WorkforceDashboardAlignmentDistribution as WorkforceDashboardAlignmentDistributionData} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardAlignmentDistributionData | null;
  fillHeight?: boolean;
}

const BUCKET_KPI_KEYS = {
  high: 'strongFit',
  medium: 'reviewNeeded',
  low: 'atRisk',
} as const;

const getBarColor = (key: string) => {
  if (key === 'high') {
    return greenColors[100];
  }
  if (key === 'medium') {
    return orangeColors[100];
  }
  return alignmentStatus.gapAccent;
};

export const WorkforceDashboardAlignmentDistribution = ({data, fillHeight}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.alignmentDistribution'});
  const {t: tPage} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage'});

  if (!data || data.totalProfiles === 0) {
    return (
      <WorkforceDashboardSectionCard title={t('title')}>
        <EmptyStateCard />
      </WorkforceDashboardSectionCard>
    );
  }

  return (
    <WorkforceDashboardSectionCard title={t('title')} fillHeight={fillHeight}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          ...(fillHeight && {flex: 1, justifyContent: 'center'}),
        }}
      >
        {data.buckets.map((bucket) => {
          const kpiKey = BUCKET_KPI_KEYS[bucket.key as keyof typeof BUCKET_KPI_KEYS];
          const bucketLabel = kpiKey
            ? tPage(`kpis.${kpiKey}.label`)
            : t(`bucketLabels.${bucket.key}`, {defaultValue: bucket.label});
          const bucketHelper = kpiKey ? tPage(`kpis.${kpiKey}.helper`) : null;

          return (
            <Box key={bucket.key} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Typography
                variant='caption'
                noWrap
                sx={{minWidth: {xs: 140, sm: 200}, flexShrink: 0, color: 'text.secondary'}}
              >
                <Box component='span' sx={{fontWeight: 600, color: 'text.primary'}}>
                  {bucketLabel}
                </Box>
                {bucketHelper ? ` · ${bucketHelper}` : ''}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 14,
                  bgcolor: 'action.hover',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    width: `${bucket.percentage}%`,
                    height: '100%',
                    bgcolor: getBarColor(bucket.key),
                    borderRadius: '6px',
                    opacity: 0.75,
                    transition: 'width .4s',
                  }}
                />
              </Box>
              <Typography variant='subtitle2' sx={{minWidth: 24, textAlign: 'right'}}>
                {bucket.count}
              </Typography>
              <Typography variant='caption' sx={{minWidth: 32, color: 'text.secondary', textAlign: 'right'}}>
                {bucket.percentage}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </WorkforceDashboardSectionCard>
  );
};
